import { EpochDeviceSplitModel } from "@/types";

/** API uses snake_case; optional camelCase supports in-app epoch device split rows. */
type DeviceClassModelInput = {
  model_name?: unknown;
  device_count?: unknown;
  gpu_count?: unknown;
  gpus_per_device?: unknown;
  supplier_count?: unknown;
  modelName?: unknown;
  deviceCount?: unknown;
  gpuCount?: unknown;
  gpuPerDevice?: unknown;
  supplierCount?: unknown;
};

/** Design-system chart themes for device-class pie (max 6 slices + Others). */
export type DeviceClassChartTheme = {
  /** Legend dot and tooltip accent */
  accent: string;
  /** Dark / shadow stop for radial pie fill */
  gradientStart: string;
  /** Bright / luminance stop for radial pie fill */
  gradientEnd: string;
};

/**
 * Six slice themes (largest → smallest): purple, cyan, 2× blue, green, yellow.
 * Others uses {@link DEVICE_CLASS_OTHERS_THEME}.
 */
export const DEVICE_CLASS_CHART_THEMES: DeviceClassChartTheme[] = [
  { accent: "#79C7FF", gradientStart: "#0761D1", gradientEnd: "#0070F3" },
  { accent: "#9061D9", gradientStart: "#140922", gradientEnd: "#6627EB" },
  { accent: "#50E3C2", gradientStart: "#247C3F", gradientEnd: "#29BC9B" },
  { accent: "#3291FF", gradientStart: "#113158", gradientEnd: "#0B92FF" },
  { accent: "#32FFB5", gradientStart: "#157839", gradientEnd: "#00CD38" },
  { accent: "#FFD232", gradientStart: "#AB570A", gradientEnd: "#F5A623" }
];

export const DEVICE_CLASS_OTHERS_THEME: DeviceClassChartTheme = {
  accent: "#999999",
  gradientStart: "#1F1F1F",
  gradientEnd: "#444444"
};

export const DEVICE_CLASS_GRADIENT_ID_PREFIX = "device-class-pie";

export const getDeviceClassGradientId = (key: string | number): string =>
  `${DEVICE_CLASS_GRADIENT_ID_PREFIX}-${key}`;

export const getDeviceClassChartTheme = (index: number): DeviceClassChartTheme =>
  DEVICE_CLASS_CHART_THEMES[index % DEVICE_CLASS_CHART_THEMES.length];

export const toFiniteNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      return 0;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const clampPercentage = (value: unknown): number => {
  return Math.min(100, Math.max(0, toFiniteNumber(value)));
};

export const sanitiseDeviceCount = (value: unknown): number => {
  return Math.max(0, Math.floor(toFiniteNumber(value)));
};

export const getDeviceClassColor = (index: number): string => {
  return getDeviceClassChartTheme(index).accent;
};

export const normaliseDeviceClassModels = (
  models: DeviceClassModelInput[] | null | undefined
): EpochDeviceSplitModel[] => {
  if (!Array.isArray(models)) {
    return [];
  }

  return models
    .map((item) => {
      const modelNameRaw = typeof item?.modelName === "string" ? item.modelName : item?.model_name;
      const modelName = typeof modelNameRaw === "string" ? modelNameRaw.trim() : "";

      const gpuRaw = item?.gpuPerDevice ?? item?.gpus_per_device;
      const gpuCountRaw = item?.gpuCount ?? item?.gpu_count;

      return {
        modelName,
        gpuPerDevice: Math.max(0, Math.round(toFiniteNumber(gpuRaw))),
        gpuCount: gpuCountRaw == null ? undefined : sanitiseDeviceCount(gpuCountRaw),
        supplierCount: sanitiseDeviceCount(item?.supplierCount ?? item?.supplier_count),
        deviceCount: sanitiseDeviceCount(item?.deviceCount ?? item?.device_count)
      };
    })
    .filter((model) => model.modelName.length > 0);
};
